import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';
import {
  DestinationType,
  ProviderType,
} from '../components/common/ObjectTypes';
import loadDestinationUrls from './destinations/loadDestinationUrls';
import { loadDestinationTypes } from './destinations/loadDestinationTypes';
import { loadProviderTypes } from './routes/loadProviderTypesFilter';
import { fetchAllPages } from './fetchAllPages';
import { queryKeys } from './queryKeys';

// The option lists behind the list pages' filter dropdowns. Each is loaded
// once and shared by every page that shows it. Technologies only change with a
// deploy and destination URLs are refreshed by the saves that change them, so
// none of them goes stale on its own; a full page reload starts fresh.
// `enabled: false` holds a list back, e.g. until the page's main table has loaded.
const useAllPagesQuery = <T>(
  queryKey: readonly unknown[],
  loadPage: (
    token: string,
    offset: number,
    accumulated: T[],
  ) => Promise<[T[], number, Error | null]>,
  enabled: boolean,
) => {
  const { keycloak } = useKeycloak();
  const token = keycloak.token;
  const query = useQuery({
    queryKey,
    queryFn: () =>
      fetchAllPages<T>((offset, accumulated) =>
        loadPage(token as string, offset, accumulated),
      ),
    enabled: enabled && !!token && !keycloak.isTokenExpired(),
    // A list cut short by a failed page loads again on the next visit.
    staleTime: (q) => (q.state.data?.partial ? 0 : Infinity),
  });
  return { data: query.data?.items ?? [], loading: query.isLoading };
};

export const useDestinationUrls = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { data, loading } = useAllPagesQuery<string>(
    queryKeys.filterOptions.destinationUrls,
    (token, offset, accumulated) =>
      loadDestinationUrls(token, offset, '', accumulated),
    enabled,
  );
  // Several connections can share a URL; the filter lists each one once.
  const destinationUrls = useMemo(() => [...new Set(data)], [data]);
  return { destinationUrls, loadingDestinationUrls: loading };
};

export const useDestinationTypes = ({
  allTypes = false,
  enabled = true,
}: { allTypes?: boolean; enabled?: boolean } = {}) => {
  const { data, loading } = useAllPagesQuery<DestinationType>(
    queryKeys.filterOptions.destinationTypes(allTypes),
    (token, offset, accumulated) =>
      loadDestinationTypes(token, offset, '', accumulated, allTypes),
    enabled,
  );
  return { destinationTypes: data, loadingDestinationTypes: loading };
};

export const useProviderTypes = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { data, loading } = useAllPagesQuery<ProviderType>(
    queryKeys.filterOptions.providerTypes,
    (token, offset, accumulated) =>
      loadProviderTypes(token, offset, '', accumulated),
    enabled,
  );
  return { providerTypes: data, loadingProviderTypes: loading };
};
